import { NavLink, useNavigate, Link } from "react-router-dom";
import { useState } from "react";
import {
  Home,
  UtensilsCrossed,
  CalendarDays,
  Box,
  Baby,
  ReceiptText,
  ShoppingCart,
  LogOut,
  LifeBuoy,
  FlaskConical,
  Menu as MenuIcon,
  X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { isAdmin } from "@/lib/adminConfig";

const NAV = [
  { to: "/", label: "Home", icon: Home, end: true },
  { to: "/menu", label: "Menu", icon: UtensilsCrossed },
  { to: "/planner", label: "Weekly Planner", icon: CalendarDays },
  { to: "/builder", label: "Build a Box", icon: Box },
  { to: "/children", label: "My Kids", icon: Baby },
  { to: "/orders", label: "Orders", icon: ReceiptText },
  { to: "/contact", label: "Contact", icon: LifeBuoy },
];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { totalQty } = useCart();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navItems = isAdmin(user)
    ? [...NAV, { to: "/admin/clover-test", label: "Clover Test", icon: FlaskConical }]
    : NAV;

  const linkClass = ({ isActive }) =>
    `inline-flex items-center gap-2 px-3.5 py-2 rounded-2xl font-semibold text-sm transition-all ${
      isActive
        ? "bg-emerald-500 text-white shadow-md shadow-emerald-200"
        : "text-slate-600 hover:bg-emerald-50 hover:text-emerald-700"
    }`;

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-md border-b-2 border-emerald-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
          <Link to="/" data-testid="brand-logo" className="flex items-center shrink-0">
            <img
              src="https://customer-assets-rejwkqb3.emergentagent.net/job_lunch-web/artifacts/sqvp4z9p_Picture1.jpg"
              alt="Take a break, we got lunch by WAJ"
              className="h-11 sm:h-12 w-auto object-contain"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} className={linkClass} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <n.icon size={16} />
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              data-testid="nav-cart"
              onClick={() => navigate("/cart")}
              className="relative inline-flex items-center justify-center h-11 w-11 rounded-2xl bg-rose-400 hover:bg-rose-500 text-white shadow-md shadow-rose-200 transition-all active:scale-95"
            >
              <ShoppingCart size={19} />
              {totalQty > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -top-1.5 -right-1.5 bg-emerald-500 text-white text-xs font-bold h-5 min-w-5 px-1 rounded-full flex items-center justify-center border-2 border-white"
                >
                  {totalQty}
                </span>
              )}
            </button>

            <button
              onClick={doLogout}
              data-testid="logout-button"
              className="hidden sm:inline-flex items-center gap-1.5 text-slate-500 hover:text-rose-500 font-semibold text-sm px-2 py-2 rounded-2xl transition-colors"
              title={user?.email}
            >
              <LogOut size={17} />
            </button>

            <button
              onClick={() => setOpen((o) => !o)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-2xl bg-emerald-50 text-emerald-700"
            >
              {open ? <X size={20} /> : <MenuIcon size={20} />}
            </button>
          </div>
        </div>

        {open && (
          <nav className="lg:hidden border-t-2 border-emerald-100 bg-white px-4 py-3 grid grid-cols-2 gap-2">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={() => setOpen(false)}
                className={linkClass}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <n.icon size={16} />
                {n.label}
              </NavLink>
            ))}
            <button
              onClick={doLogout}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-2xl font-semibold text-sm text-rose-500 hover:bg-rose-50"
            >
              <LogOut size={16} /> Log out
            </button>
          </nav>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">{children}</main>
    </div>
  );
};
